import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { AlertInstance, StudentListRow } from '../../../api/types';
import { nameOr } from '../../../lib/names';
import { alertSentence } from '../../../lib/alertSentence';
import { ago, initials } from './format';
import { alertPlace } from './useStudentLookup';

const NO_STUDENTS = new Map<number, StudentListRow>();

const RANK = { critical: 0, warning: 1, info: 2 } as Record<string, number>;

export default function NeedsYouToday({ alerts, title = 'Needs you today', students = NO_STUDENTS }: { alerts: AlertInstance[]; title?: string; students?: Map<number, StudentListRow> }) {
  const sorted = [...alerts].sort((a, b) => (RANK[a.severity] ?? 3) - (RANK[b.severity] ?? 3) || b.fired_at.localeCompare(a.fired_at));
  const top = sorted.slice(0, 3);
  const critical = alerts.filter((a) => a.severity === 'critical').length;
  const headline = critical > 0 ? critical : alerts.length;
  const noun = critical > 0 ? (critical === 1 ? 'critical alert' : 'critical alerts') : (alerts.length === 1 ? 'open alert' : 'open alerts');
  const rest = alerts.length - top.length;
  return (
    <section aria-label={title} className="flex min-w-0 flex-col gap-4 rounded-[22px] bg-navy p-6 text-white">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-on-navy-muted">{title}</div>
          {alerts.length > 0 && (
            <div className="mt-2.5 flex items-baseline gap-2.5">
              <span className="font-mono text-[52px] font-semibold leading-none tracking-[-0.04em]">{headline}</span>
              <span className="font-display text-lg font-bold">{noun}</span>
            </div>
          )}
        </div>
        {rest > 0 && <span className="flex-none whitespace-nowrap rounded-full bg-navy-2 px-2 py-[3px] font-mono text-xs font-semibold text-on-navy">+{rest} others open</span>}
      </div>
      {alerts.length === 0 ? (
        <p className="py-6 text-[15px] text-on-navy">Nothing needs you right now</p>
      ) : (
        <div className="flex flex-col gap-2">
          {top.map((a) => {
            const name = nameOr(a.student_name, a.student_username);
            const place = alertPlace(a, students);
            return (
              <Link key={a.id} to={`/students/profile/${a.student_id}`}
                className="flex items-center gap-3 rounded-[14px] bg-navy-2 p-3 text-white no-underline hover:bg-navy-3 hover:text-white">
                <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[12px] bg-mist text-xs font-semibold text-ink">{initials(name)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">{name} {place && <span className="font-normal text-on-navy-muted">· {place}</span>}</span>
                  <span className="block truncate text-[13px] text-on-navy-muted">{alertSentence(a)}</span>
                </span>
                <span className="whitespace-nowrap rounded-full bg-tag-bg px-2 py-[3px] text-[11px] font-semibold text-tag-text">{ago(a.fired_at)}</span>
              </Link>
            );
          })}
        </div>
      )}
      <Link to="/alerts" className="mt-auto inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[12px] bg-accent px-4 text-sm font-semibold text-navy no-underline hover:text-navy hover:brightness-105">
        Review in alerts inbox <ArrowRight size={18} strokeWidth={1.8} aria-hidden />
      </Link>
    </section>
  );
}
